import { Text } from "@ecco/ui/text";
import type { MoneyProps } from "@ecco/ui/money";
import { Money } from "@ecco/ui/money";
import { Box } from "@ecco/ui/box";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import { Tooltip } from "@ecco/ui/tooltip";
import { useTooltip } from "@ecco/ui/use-tooltip";
import { useRef, type ReactNode, type JSX } from "react";
import { styles } from "./SummaryRowStyles";
import { getPrivacyAttributes } from "@lib/monitoring/privacyAttributes";

export const summaryRowTestId = "summary-row";
export const rowHeaderTextTestId = "row-header-text";

interface SummaryRowProps {
    title: string | ReactNode;
    moneyProps?: MoneyProps;
    containerStyle?: object;
    textStyle?: object;
    tooltipInfo?: ReactNode;
    toReplacement?: ReactNode;
}

const SummaryRow = ({
    title,
    moneyProps,
    toReplacement,
    containerStyle,
    tooltipInfo,
    textStyle,
}: SummaryRowProps): JSX.Element => {
    const tooltipRef = useRef(undefined);

    const { isTooltipOpen, setIsTooltipOpen } = useTooltip({ tooltipRef });

    return (
        <Box
            {...getPrivacyAttributes()}
            sx={{ ...styles.container, ...containerStyle }}
            data-testid={summaryRowTestId}
        >
            <Flex gap={1} direction="row-reverse">
                <Text
                    as={"span"}
                    fontSize={["mobileBodyTextNormal", "desktopBodyTextNormal"]}
                    fontWeight="bold"
                    sx={{
                        ...styles.part,
                        ...textStyle,
                    }}
                    data-testid={rowHeaderTextTestId}
                >
                    {title}
                </Text>
                {tooltipInfo && (
                    <Tooltip
                        isOpen={isTooltipOpen}
                        hasArrow={false}
                        label={tooltipInfo}
                        closeOnScroll={true}
                        aria-label={typeof title === "string" && title}
                        sx={styles.tooltip({ offset: [60, 80, 100] })}
                    >
                        <ECCOIcon
                            ref={tooltipRef}
                            name={"info"}
                            onClick={() => setIsTooltipOpen(!isTooltipOpen)}
                            sx={styles.tooltipIcon}
                        />
                    </Tooltip>
                )}
            </Flex>
            {toReplacement || (
                <Money as={"span"} sx={styles.part} {...moneyProps} data-testid="summarymoney" />
            )}
        </Box>
    );
};

export default SummaryRow;
